import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const KEY = "careable_admin_token";
const EMAIL_KEY = "careable_admin_email";

const AuthContext = createContext({
  token: null,
  email: null,
  loading: true,
  login: async () => {},
  logout: () => {},
});

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(KEY));
  const [email, setEmail] = useState(() => localStorage.getItem(EMAIL_KEY));
  const [loading, setLoading] = useState(true);

  // Validate token on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        await api.get("/admin/me", { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setLoading(false);
      } catch {
        if (!cancelled) {
          localStorage.removeItem(KEY);
          localStorage.removeItem(EMAIL_KEY);
          setToken(null);
          setEmail(null);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (emailVal, password) => {
    const { data } = await api.post("/admin/login", { email: emailVal, password });
    localStorage.setItem(KEY, data.access_token);
    localStorage.setItem(EMAIL_KEY, data.email);
    setToken(data.access_token);
    setEmail(data.email);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(KEY);
    localStorage.removeItem(EMAIL_KEY);
    setToken(null);
    setEmail(null);
  }, []);

  return (
    <AuthContext.Provider value={{ token, email, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function authHeaders() {
  const t = localStorage.getItem(KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
